import { api, getAuthorizationToken } from 'pages/APIs';
import AbstractApiService from 'src/services/abstractApiService';
import { docScriptUpdate, docScriptTest, docScript } from 'src/types';

import axios from 'axios';
import { createCancelTokenHandler } from 'src/core/cancelRequestHandler';
import type { dynamicObj } from 'src/pages/assets';
import { addOrgIdQueryString } from 'src/utils/organization/org_id_helper';

const cancelTokenHandlerObject = createCancelTokenHandler('applyScript');

export default class DocScriptApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async getByDocTypeSubDocType(
    doc_type: string,
    data: dynamicObj,
  ): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(
      api.get_document_type_script + doc_type + '?' + querystring,
      {},
    );
  }

  public async save(data: any): Promise<any> {
    const formData = new FormData();
    formData.append('data', data);
    return super.post(api.save_special_rules, formData);
  }

  public async getByID(id: string): Promise<any> {
    let url = api.get_script_by_id + id;
    url = addOrgIdQueryString(url);
    return super.get(url);
  }

  public async getByDocType(doc_type: string): Promise<any> {
    return super.get(api.get_document_type_script + doc_type);
  }

  createUpdateScript(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.update_document_type_script, formData);
  }

  public async createDocTypeScript(data: docScriptUpdate): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.update_document_type_script, formData);
  }

  public async TestScriptWithDocJson(data: docScriptTest): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.test_document_type_script, formData);
  }

  public async copyScript(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.document_clone_script, formData);
  }

  public async deleteRecord(id: string): Promise<any> {
    let url = api.delete_document_script + id;
    url = addOrgIdQueryString(url);
    return this.delete(url, {});
  }

  public async updateActive(id: string, is_active: boolean): Promise<any> {
    const formData = new FormData();
    formData.append('activate', is_active === true ? 'true' : 'false');
    return super.post(api.script_activate_deactivate + id, formData);
  }

  // TODO: Shift to proper place later when time permits
  public async applyScript(data: docScript): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    formData.append('org_id', this.getOrgId());

    const token: string = await getAuthorizationToken();
    const response = await axios
      .post(api.apply_script, formData, {
        cancelToken:
          cancelTokenHandlerObject['applyScript'].handleRequestCancellation()
            .token,
        headers: {
          Authorization: token,
        },
      })
      .catch(function (thrown) {
        if (axios.isCancel(thrown) && thrown.message === 'applyScript') {
          return false;
        }
      });

    return response;
  }

  public async markAsActive(script_id: any): Promise<any> {
    const formData = new FormData();
    formData.set('script_id', script_id);
    return this.post(api.mark_as_active, formData);
  }

  public async getScriptVersions(script_id: any): Promise<any> {
    return this.get(api.get_script_versions + script_id, {});
  }

  public async createScriptDraft(script_version_id: any): Promise<any> {
    const formData = new FormData();
    formData.set('version_id', script_version_id);
    return this.post(api.create_script_draft, formData);
  }

  public async deleteScriptDraft(script_version_id: any): Promise<any> {
    return this.delete(api.delete_script_draft + script_version_id, {});
  }

  public async restoreVersion(version_id: string): Promise<any> {
    const formData = new FormData();
    return this.post(api.restore_version + version_id, formData);
  }
}
